/**
 * Admin Networks Management Module
 *
 * Network CRUD for the Manage Networks page. Operators define [network.X]
 * only — the daemon owns the single vmsman-br bridge, so there is no bridge
 * authoring here. (Externally-managed bridges, the direct-attach escape
 * hatch, are adopted via the CLI/API, not this page.)
 */

import { api } from '../api/client.js';
import type { BridgeDetail, BridgeSambaConfig, CreateNetworkRequest, NetworkDetail } from '../api/types.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog } from '../ui/dialogs.js';
import { generateToggle, generateToggleList, getToggleListSelected, generateComboSelect } from '../ui/forms.js';

let networksTable: unknown = null;
let selectedHost: string = '';  // '' = local

/**
 * Get the currently selected network host ('' = local)
 */
export function getSelectedNetHost(): string {
  return selectedHost;
}

/**
 * Set the selected host and reload the networks table
 */
export async function setNetHost(host: string): Promise<void> {
  selectedHost = host;
  declareLoading();
  await fillNetworksTable();
  declareLoaded();
}

/**
 * The name of the single daemon-owned managed bridge. Networks live here.
 */
const MANAGED_BRIDGE = 'vmsman-br';

/**
 * Look up the managed bridge's uplinks (used as the candidate set for a
 * network's per-VLAN uplink subset). Falls back to an empty list.
 */
async function getManagedBridgeUplinks(host: string | undefined): Promise<string[]> {
  const bridges: BridgeDetail[] = await apiCall(api.getNetBridges(host), '').then(b => b || []).catch(() => []);
  const mb = bridges.find(br => br.name === MANAGED_BRIDGE);
  return mb?.uplinks || [];
}

/**
 * Fill the networks table with [network.X] entries from /api/net/networks.
 */
export async function fillNetworksTable(): Promise<void> {
  const host = selectedHost || undefined;
  const networks: NetworkDetail[] | null = await apiCall(
    api.getNetNetworks(host), 'Failed to Load Networks'
  );
  if (!networks) return;

  // Samba config is still stored bridge-keyed in the backend. All networks
  // sit on vmsman-br, so a single lookup covers AD presence for the page.
  const uniqueBridges = Array.from(new Set(networks.map(n => n.bridge).filter((b): b is string => !!b)));
  const sambaByBridge: Record<string, BridgeSambaConfig | null> = {};
  await Promise.all(uniqueBridges.map(async (br) => {
    sambaByBridge[br] = await apiCall(api.getBridgeSamba(br, host), '').catch(() => null);
  }));

  const tableData = networks.map((net) => {
    const safeName = escapeAttr(net.name);
    const flags: string[] = [];
    if (net.dhcp_server) flags.push('<span class="badge badge-success">dhcp</span>');
    if (net.block_lan) flags.push('<span class="badge badge-warn">block-lan</span>');
    if (net.hairpin_nat) flags.push('<span class="badge badge-info">hairpin</span>');
    // AD DC presence — keyed by the network's parent bridge.
    const samba = net.bridge ? sambaByBridge[net.bridge] : null;
    if (samba?.enabled && samba?.domain) {
      flags.push(`<span class="badge badge-info">AD ${escapeHtml(samba.domain)}</span>`);
    }
    // Uplinks badge: absent = "all" (no badge); [] = isolated; [...] = subset count.
    if (Array.isArray(net.uplinks)) {
      if (net.uplinks.length === 0) {
        flags.push('<span class="badge badge-warn">isolated</span>');
      } else {
        flags.push(`<span class="badge badge-info">${net.uplinks.length} uplink${net.uplinks.length === 1 ? '' : 's'}</span>`);
      }
    }

    return {
      name: escapeHtml(net.name),
      vlan: net.vlan_id ? String(net.vlan_id) : 'untagged',
      cidr: net.cidr ? escapeHtml(net.cidr) : '-',
      domain: net.domain ? escapeHtml(net.domain) : '-',
      flags: flags.length ? flags.join(' ') : '-',
      actions: `
        <button class="ui-button" onclick='modifyNetwork("${safeName}")'>Modify</button>
        <button class="ui-button" onclick='deleteNetwork("${safeName}")'>Delete</button>
      `,
    };
  });

  if (!networksTable) {
    networksTable = $('#networks').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'vlan', title: 'VLAN' },
        { data: 'cidr', title: 'CIDR' },
        { data: 'domain', title: 'Domain' },
        { data: 'flags', title: 'Flags' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = networksTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }

  declareLoaded();
}

/**
 * Setup samba toggle visibility
 */
function setupSambaToggle(): void {
  $('#samba-enabled').on('change', function() {
    const enabled = $(this).is(':checked');
    if (enabled) {
      $('#samba-config-section').slideDown();
    } else {
      $('#samba-config-section').slideUp();
    }
  });
}

/**
 * Show/hide the per-uplink checkboxes based on the mode select.
 */
function setupNetworkUplinksToggle(): void {
  $('#net-uplinks-mode').on('change', function() {
    const mode = $(this).val() as string;
    if (mode === 'custom') {
      $('#net-uplinks-custom-section').slideDown();
    } else {
      $('#net-uplinks-custom-section').slideUp();
    }
  });
}

/**
 * Collect samba config from form.
 */
function collectSambaForm(): Partial<BridgeSambaConfig> {
  const enabled = $('#samba-enabled').is(':checked');
  if (!enabled) {
    return { enabled: false };
  }

  const result: Partial<BridgeSambaConfig> = {
    enabled: true,
    managed: $('#samba-managed').is(':checked'),
    domain: ($('#samba-domain').val() as string || '').trim(),
    workgroup: ($('#samba-workgroup').val() as string || '').trim() || undefined,
    admin_user: ($('#samba-admin-user').val() as string || '').trim(),
  };

  const password = ($('#samba-admin-password').val() as string || '').trim();
  if (password) {
    result.admin_password = password;
  }

  return result;
}

/**
 * Re-apply network configuration (POST /api/net/apply → netmgr_setup).
 */
export async function applyNetworks(): Promise<void> {
  const host = selectedHost || undefined;
  declareLoading();
  const result = await apiCall(api.applyNetworks(host), 'Failed to Apply Network Configuration');
  declareLoaded();
  if (result) {
    const { notificationDialog } = await import('../ui/dialogs.js');
    notificationDialog(
      '<p>Networks applied successfully.</p>' +
      '<p style="margin-top:1em;color:#888;font-size:0.9em;">' +
      '<strong>Note:</strong> VLAN changes on the managed bridge may require a ' +
      'restart to take full effect.</p>',
      true  // allowHtml
    );
  }
}

/**
 * Build the network form HTML. Networks are the only operator-facing entity
 * and always live on the single daemon-owned bridge (vmsman-br); each carries
 * a VLAN ID (0 = untagged). L3 (CIDR/DHCP/domain/NAT) is optional per network.
 *
 * Samba config is bridge-keyed in the backend but presented per-network here.
 */
async function buildNetworkForm(network?: NetworkDetail, sambaConfig?: BridgeSambaConfig | null): Promise<string> {
  const host = selectedHost || undefined;
  // Fetch the managed bridge's uplinks (candidate set for the per-VLAN uplink
  // subset) and default-gateway-bearing interfaces (NAT uplink dropdown).
  const [candidateUplinks, gwIfaces, orgList] = await Promise.all([
    getManagedBridgeUplinks(host),
    apiCall(api.getDefaultGatewayInterfaces(host), '').then(g => g || []).catch(() => [] as string[]),
    apiCall(api.listOrganizations(), '').then(r => r?.organizations || []).catch(() => []),
  ]);

  // Organization scoping: an org-less network is "shared" and visible on
  // every vhost portal; scoping it to an org hides it from other portals.
  const orgOpts: Record<string, string> = { '': '(shared — visible to all organizations)' };
  for (const o of orgList) orgOpts[o.name] = o.name;
  const orgVal = network?.org || '';

  const name = network?.name || '';
  const vlanId = network?.vlan_id ?? '';
  const cidr = network?.cidr || '';
  const gateway = network?.gateway || '';
  const hostGateway = network?.host_gateway || '';
  const domain = network?.domain || '';
  const dhcpServer = network?.dhcp_server || false;
  const dhcpStart = network?.dhcp_range_start || '';
  const dhcpEnd = network?.dhcp_range_end || '';
  const blockLan = network?.block_lan || false;
  const hairpinNat = network?.hairpin_nat || false;
  const natUplink = network?.nat_uplink || '';
  const dhcpClient = network?.dhcp_client || false;

  // Tri-state uplinks: undefined = "use all bridge uplinks" (default);
  // [] = explicit isolation; [...] = explicit subset.
  const uplinksField = network?.uplinks;
  const uplinksMode: 'default' | 'custom' =
    uplinksField === undefined ? 'default' : 'custom';
  const checkedUplinks: string[] =
    uplinksField === undefined ? candidateUplinks : uplinksField;
  let uplinksToggleListHtml = '';
  if (candidateUplinks.length > 0) {
    uplinksToggleListHtml = generateToggleList('net-uplink', candidateUplinks, checkedUplinks);
  } else {
    uplinksToggleListHtml =
      '<span style="color:#888">The managed bridge has no uplinks configured.</span>';
  }

  // Samba config values
  const sambaEnabled = sambaConfig?.enabled || false;
  const sambaManaged = sambaConfig?.managed ?? true;
  const sambaDomain = sambaConfig?.domain || '';
  const sambaWorkgroup = sambaConfig?.workgroup || '';
  const sambaAdminUser = sambaConfig?.admin_user || 'Administrator';

  // Peer network options for routed/nat lists (all networks except this one)
  const peerNames = (await apiCall(api.getNetNetworks(host), '').then(n => n || []))
    .map(n => n.name)
    .filter(n => n !== name);
  const selectedRouted = network?.routed_networks || [];
  const selectedNat = network?.nat_networks || [];
  let routedHtml = '';
  let natHtml = '';
  if (peerNames.length > 0) {
    routedHtml = generateToggleList('net-routed', peerNames, selectedRouted);
    natHtml = generateToggleList('net-nat', peerNames, selectedNat);
  } else {
    const noOther = '<span style="color:#888">No other networks configured</span>';
    routedHtml = noOther;
    natHtml = noOther;
  }

  return `
    <p style="color: var(--color-text-muted); font-size: 0.9em;">
      Networks live on the daemon-owned <strong>${MANAGED_BRIDGE}</strong> bridge.
      Give each a VLAN ID (0 = untagged); leave the L3 fields blank for a pure
      L2 segment.
    </p>
    <div class="form-field"><label>Name</label><input id="net-name" value="${escapeAttr(name)}" ${network ? 'readonly' : ''}></div>
    <div class="form-field"><label>Organization</label>${generateComboSelect('net-org', orgVal, orgOpts)}
      <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
        Scopes this network to one organization's portal. Leave "shared" to show it on every vhost.
        Networks created through an org portal are scoped to that org automatically.
      </div>
    </div>
    <div class="form-field"><label>VLAN ID</label><input id="net-vlan" type="number" min="0" max="4094" value="${escapeAttr(String(vlanId))}" placeholder="0 = untagged">
      <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
        VLAN tag on the bridge. 0 = untagged (only one untagged network is allowed).
      </div>
    </div>
    <div class="form-field"><label>DHCP Client</label><div class="form-value">${generateToggle('net-dhcp-client', dhcpClient)}</div>
      <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
        Get the address from an upstream DHCP server. Mutually exclusive with CIDR.
      </div>
    </div>
    <div class="form-field"><label>CIDR</label><input id="net-cidr" value="${escapeAttr(cidr)}" placeholder="e.g. 10.10.0.1/24 (blank = L2-only)"></div>
    <div class="form-field"><label>Gateway</label><input id="net-gateway" value="${escapeAttr(gateway)}" placeholder="DHCP default route for VMs (defaults to network IP)"></div>
    <div class="form-field"><label>Host Gateway</label><input id="net-host-gateway" value="${escapeAttr(hostGateway)}" placeholder="Optional route on host via this network"></div>
    <div class="form-field"><label>Domain</label><input id="net-domain" value="${escapeAttr(domain)}" placeholder="e.g. lab.local"></div>
    <div class="form-field"><label>DHCP Server</label><div class="form-value">${generateToggle('net-dhcp', dhcpServer)}</div></div>
    <h4>DHCP Range</h4>
    <div class="form-field"><label>Start</label><input id="net-dhcp-start" value="${escapeAttr(dhcpStart)}" placeholder="e.g. 10.10.0.100"></div>
    <div class="form-field"><label>End</label><input id="net-dhcp-end" value="${escapeAttr(dhcpEnd)}" placeholder="e.g. 10.10.0.200"></div>

    <h4>Physical Uplinks (VLAN carriers)</h4>
    <div style="font-size: 0.85em; color: var(--color-text-muted); margin-bottom: 6px;">
      Which of the managed bridge's uplinks carry this network's VLAN.
      "Default" leaves the field unset and lets the daemon use all bridge uplinks.
      "Custom" lets you restrict — including the empty case for software-only isolation.
    </div>
    <div class="form-field">
      <label>Mode</label>
      <select id="net-uplinks-mode">
        <option value="default" ${uplinksMode === 'default' ? 'selected' : ''}>Default (all bridge uplinks)</option>
        <option value="custom" ${uplinksMode === 'custom' ? 'selected' : ''}>Custom subset</option>
      </select>
    </div>
    <div id="net-uplinks-custom-section" style="display: ${uplinksMode === 'custom' ? 'block' : 'none'}">
      ${uplinksToggleListHtml}
      <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
        Uncheck all to mark this network as isolated (no physical egress).
      </div>
    </div>

    <div class="form-field"><label>NAT Uplink</label>${(() => {
      const opts: Record<string, string> = { '': '(none — no NAT egress)' };
      for (const iface of gwIfaces) opts[iface] = iface;
      // If a previously-saved NAT uplink no longer has a default gateway,
      // keep it as a selectable option so editing the network doesn't
      // silently overwrite the field. Tagged "(no default route)" so the
      // operator knows it's been demoted.
      if (natUplink && !gwIfaces.includes(natUplink)) {
        opts[natUplink] = `${natUplink} (no default route)`;
      }
      return generateComboSelect('net-nat-uplink', natUplink, opts);
    })()}
      <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
        Upstream interface used for MASQUERADE egress. Only interfaces with a default route are listed.
      </div>
    </div>
    <div class="form-field"><label>Block LAN on NAT</label><div class="form-value">${generateToggle('net-block-lan', blockLan)}</div></div>
    <div class="form-field"><label>Hairpin NAT</label><div class="form-value">${generateToggle('net-hairpin-nat', hairpinNat)}</div></div>
    <h4>Routed Networks</h4>
    ${routedHtml}
    <h4>NAT Networks</h4>
    ${natHtml}

    <h4>Samba AD Domain Controller</h4>
    <div class="form-field"><label>Enable AD DC</label><div class="form-value">${generateToggle('samba-enabled', sambaEnabled)}</div></div>
    <div id="samba-config-section" style="display: ${sambaEnabled ? 'block' : 'none'}">
      <div class="form-field"><label>AD Domain</label><input id="samba-domain" value="${escapeAttr(sambaDomain)}" placeholder="e.g. EXAMPLE.COM"></div>
      <div class="form-field"><label>NetBIOS Name</label><input id="samba-workgroup" value="${escapeAttr(sambaWorkgroup)}" placeholder="Optional (derived from domain)"></div>
      <div class="form-field"><label>Admin User</label><input id="samba-admin-user" value="${escapeAttr(sambaAdminUser)}"></div>
      <div class="form-field"><label>Admin Password</label><input id="samba-admin-password" type="password" placeholder="Required for new DC"></div>
      <div class="form-field"><label>Managed DC</label><div class="form-value">${generateToggle('samba-managed', sambaManaged)}</div></div>
    </div>
  `;
}

/**
 * Collect network form values into a CreateNetworkRequest. The daemon pins
 * every network to vmsman-br, so no bridge field is sent.
 */
function collectNetworkForm(): CreateNetworkRequest {
  const result: Record<string, unknown> = {};
  result.name = ($('#net-name').val() as string || '').trim();
  const org = ($('#net-org').val() as string || '').trim();
  result.org = org || null;
  const vlan = parseInt($('#net-vlan').val() as string || '0', 10);
  result.vlan_id = isNaN(vlan) ? 0 : vlan;
  const cidr = ($('#net-cidr').val() as string || '').trim();
  result.cidr = cidr || null;
  const gw = ($('#net-gateway').val() as string || '').trim();
  result.gateway = gw || null;
  const hgw = ($('#net-host-gateway').val() as string || '').trim();
  result.host_gateway = hgw || null;
  const domain = ($('#net-domain').val() as string || '').trim();
  result.domain = domain || null;
  result.dhcp_server = $('#net-dhcp').is(':checked');
  result.dhcp_client = $('#net-dhcp-client').is(':checked');
  const dhcpStart = ($('#net-dhcp-start').val() as string || '').trim();
  const dhcpEnd = ($('#net-dhcp-end').val() as string || '').trim();
  if (dhcpStart && dhcpEnd) {
    result.dhcp_range_start = dhcpStart;
    result.dhcp_range_end = dhcpEnd;
  } else {
    result.dhcp_range_start = null;
    result.dhcp_range_end = null;
  }
  const natUplink = ($('#net-nat-uplink').val() as string || '').trim();
  result.nat_uplink = natUplink || null;
  result.block_lan = $('#net-block-lan').is(':checked');
  result.hairpin_nat = $('#net-hairpin-nat').is(':checked');
  result.routed_networks = getToggleListSelected('net-routed');
  result.nat_networks = getToggleListSelected('net-nat');

  // Tri-state uplinks:
  //   - mode = "default" → send `null` so the daemon clears any prior
  //     subset; the field round-trips as "key absent" on a subsequent GET.
  //   - mode = "custom" → send the checked set (possibly empty for
  //     explicit isolation).
  const uplinksMode = $('#net-uplinks-mode').val() as string;
  if (uplinksMode === 'custom') {
    result.uplinks = getToggleListSelected('net-uplink');
  } else {
    result.uplinks = null;
  }
  return result as unknown as CreateNetworkRequest;
}

function validateNetworkForm(data: Partial<NetworkDetail>): string | null {
  if (!data.name) return 'Name is required';
  const routed = data.routed_networks || [];
  const nat = data.nat_networks || [];
  const overlap = routed.filter(n => nat.includes(n));
  if (overlap.length > 0) {
    return `Network "${overlap[0]}" cannot appear in both Routed Networks and NAT Networks`;
  }
  return null;
}

export async function createNetwork(): Promise<void> {
  declareLoading();
  const form = await buildNetworkForm();
  declareLoaded();

  const host = selectedHost || undefined;
  let saving = false;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Create Network</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Create Network',
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      open: function() {
        setupSambaToggle();
        setupNetworkUplinksToggle();
      },
      buttons: {
        Create: async function () {
          if (saving) return;
          saving = true;
          const data = collectNetworkForm();
          const err = validateNetworkForm(data);
          if (err) {
            alert(err);
            saving = false;
            return;
          }
          const sambaData = collectSambaForm();
          if (sambaData.enabled && !sambaData.domain) {
            alert('AD Domain is required when Samba AD DC is enabled');
            saving = false;
            return;
          }
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.createNetwork(data, host), 'Failed to Create Network');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }
          // Samba config is bridge-keyed in the backend — look up the
          // new network's parent bridge from the response.
          if (sambaData.enabled) {
            const created = result as { bridge?: string };
            const bridgeName = created.bridge;
            if (bridgeName) {
              await apiCall(api.updateBridgeSamba(bridgeName, sambaData, host), 'Failed to Save Samba Config');
            }
          }
          declareLoaded();
          parent.dialog('close');
          fillNetworksTable();
        },
        Cancel: function () { $(this).dialog('close'); },
      },
      close: function () { $(this).remove(); },
    });
}

export async function modifyNetwork(name: string): Promise<void> {
  declareLoading();
  const host = selectedHost || undefined;
  const network = await apiCall(api.getNetwork(name, host), 'Failed to Load Network');
  if (!network) { declareLoaded(); return; }
  // Samba config is bridge-keyed; fetch by the network's parent bridge.
  const sambaConfig = network.bridge
    ? await apiCall(api.getBridgeSamba(network.bridge, host), '').catch(() => null)
    : null;
  const form = await buildNetworkForm(network, sambaConfig);
  declareLoaded();

  let saving = false;
  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Modify Network</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Modify Network: ' + escapeHtml(name),
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      open: function() {
        setupSambaToggle();
        setupNetworkUplinksToggle();
      },
      buttons: {
        Save: async function () {
          if (saving) return;
          saving = true;
          const data = collectNetworkForm();
          const err = validateNetworkForm(data);
          if (err) {
            alert(err);
            saving = false;
            return;
          }
          const sambaData = collectSambaForm();
          if (sambaData.enabled && !sambaData.domain) {
            alert('AD Domain is required when Samba AD DC is enabled');
            saving = false;
            return;
          }
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.updateNetwork(name, data, host), 'Failed to Update Network');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }
          // Update Samba on the (possibly changed) parent bridge.
          const updated = result as { bridge?: string };
          const bridgeName = updated.bridge || network.bridge;
          const wasEnabled = sambaConfig?.enabled || false;
          const isEnabled = sambaData.enabled || false;
          if (bridgeName && (wasEnabled || isEnabled)) {
            await apiCall(api.updateBridgeSamba(bridgeName, sambaData, host), 'Failed to Save Samba Config');
          }
          declareLoaded();
          parent.dialog('close');
          fillNetworksTable();
        },
        Cancel: function () { $(this).dialog('close'); },
      },
      close: function () { $(this).remove(); },
    });
}

export async function deleteNetwork(name: string): Promise<void> {
  const host = selectedHost || undefined;
  confirmDialog(
    `Are you sure you want to delete network '${escapeHtml(name)}'?`,
    async () => {
      declareLoading();
      await apiCall(api.deleteNetwork(name, host), 'Failed to Delete Network');
      await fillNetworksTable();
      declareLoaded();
    }
  );
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillNetworksTable = fillNetworksTable;
  (window as unknown as Record<string, unknown>).applyNetworks = applyNetworks;
  (window as unknown as Record<string, unknown>).setNetHost = setNetHost;
  (window as unknown as Record<string, unknown>).createNetwork = createNetwork;
  (window as unknown as Record<string, unknown>).modifyNetwork = modifyNetwork;
  (window as unknown as Record<string, unknown>).deleteNetwork = deleteNetwork;
}
